import React, { useEffect, useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@os/components/ui/dialog';
import { Button } from '@os/components/ui/button';
import { Input } from '@os/components/ui/input';
import { supabase } from '@os/integrations/supabase/client';
import { useAuth } from '@os/hooks/useAuth';
import { useToast } from '@os/components/ui/use-toast';
import { Card } from '@os/components/ui/card';

type ProjectRow = { id: string; name: string; description?: string | null };

type Props = {
  open: boolean;
  onClose: () => void;
  onLinked: (projectId: string, projectName: string) => void;
};

const MapleWordLinkProjectDialog: React.FC<Props> = ({ open, onClose, onLinked }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchProjects = async () => {
      if (!user) return;
      setLoading(true);
      const { data, error } = await supabase
        .from('projects')
        .select('id, name, description')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      setLoading(false);
      if (error) {
        toast({ title: 'Error', description: 'Failed to load projects.', variant: 'destructive' });
        return;
      }
      setProjects(data || []);
    };
    if (open) fetchProjects();
  }, [open, user, toast]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter(p => p.name.toLowerCase().includes(q));
  }, [projects, search]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Link Document to a Project</DialogTitle>
          <DialogDescription>
            This chat isn’t assigned to a Project. Select a Project to link and mark the document as Completed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search projects…"
            className="h-9"
          />
          <div className="max-h-64 overflow-y-auto space-y-2">
            {loading ? (
              <div className="text-sm text-muted-foreground">Loading projects…</div>
            ) : filtered.length === 0 ? (
              <div className="text-sm text-muted-foreground">No projects found.</div>
            ) : (
              filtered.map((p) => (
                <Card key={p.id} className="p-3 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{p.name}</div>
                    {p.description ? (
                      <div className="text-xs text-muted-foreground truncate">{p.description}</div>
                    ) : null}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => onLinked(p.id, p.name)}>
                    Link
                  </Button>
                </Card>
              ))
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MapleWordLinkProjectDialog;